import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Menu, X, LogOut, Sparkles } from "lucide-react";
import { ASSETS } from "@/lib/gp";
import { useAuth } from "@/context/AuthContext";
import NotificationBell from "@/components/NotificationBell";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const NAV = [
  { label: "Play", to: "/chat" },
  { label: "Tournaments", to: "/tournaments" },
  { label: "Balatro", to: "/balatro" },
  { label: "GTA 6", to: "/gta-6" },
  { label: "News", to: "/news" },
  { label: "Support", to: "/support" },
];

const PLAN_LABEL = { rookie: "Rookie", pro: "Pro", legend: "Legend" };

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const doLogout = async () => { await logout(); navigate("/"); };
  const initials = (user && (user.name || user.email || "?").slice(0, 1).toUpperCase()) || "?";

  return (
    <header className="sticky top-0 z-50 glass border-b border-white/10" data-testid="navbar">
      <div className="mx-auto max-w-[1440px] px-5 md:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group" data-testid="nav-logo">
          <img src={ASSETS.logo} alt="GamePlat" className="h-11 w-12 object-contain drop-shadow-[0_0_8px_rgba(0,157,255,0.35)]" />
          <span className="font-unbounded font-black tracking-tighter text-xl">
            Game <span className="text-gp-blue text-glow-blue">Plat</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                pathname === n.to || pathname.startsWith(`${n.to}/`) ? "text-gp-blue" : "text-white/70 hover:text-white"
              }`}
            >
              {n.label}
            </Link>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <NotificationBell />
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu" className="flex items-center gap-2.5 rounded-full pl-1 pr-3 py-1 hover:bg-white/5 transition-colors">
                  {user.picture ? (
                    <img src={user.picture} alt="" className="h-8 w-8 rounded-full object-cover" />
                  ) : (
                    <span className="grid place-items-center h-8 w-8 rounded-full bg-gp-blue text-white font-bold text-sm">{initials}</span>
                  )}
                  <span className="text-sm font-medium max-w-[120px] truncate">{user.name || user.email}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-gp-surface border-white/10 text-white w-56">
                <DropdownMenuLabel className="truncate">{user.email}</DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem className="focus:bg-white/10 cursor-default">
                  <Sparkles size={15} className="mr-2 text-gp-gold" /> Plan: {PLAN_LABEL[user.plan] || "Rookie"}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-profile" className="focus:bg-white/10 cursor-pointer">
                  My profile
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/pricing")} className="focus:bg-white/10 cursor-pointer">
                  Manage plan
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem onClick={doLogout} data-testid="logout-btn" className="focus:bg-white/10 cursor-pointer text-gp-red">
                  <LogOut size={15} className="mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link to="/login" data-testid="nav-signin" className="text-sm font-medium text-white/80 hover:text-white px-3">
              Sign in
            </Link>
          )}
          <Button
            data-testid="nav-cta"
            onClick={() => navigate("/chat")}
            className="rounded-full bg-gp-blue hover:bg-gp-blue/90 text-white font-semibold px-5 shadow-[0_0_18px_rgba(22,119,255,0.45)] transition-transform hover:-translate-y-0.5"
          >
            How can I help you win?
          </Button>
        </div>

        <div className="md:hidden flex items-center gap-1">
          <NotificationBell />
          <button
            className="p-2 text-white"
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden glass border-t border-white/10 px-5 py-4 flex flex-col gap-1" data-testid="nav-mobile-menu">
          {NAV.map((n) => (
            <Link key={n.to} to={n.to} onClick={() => setOpen(false)} className="px-3 py-3 rounded-lg text-white/80 hover:bg-white/5">
              {n.label}
            </Link>
          ))}
          {user ? (
            <button onClick={() => { setOpen(false); doLogout(); }} className="px-3 py-3 rounded-lg text-left text-gp-red hover:bg-white/5">
              Sign out ({user.name || user.email})
            </button>
          ) : (
            <Link to="/login" onClick={() => setOpen(false)} className="px-3 py-3 rounded-lg text-gp-blue hover:bg-white/5">
              Sign in
            </Link>
          )}
          <Button onClick={() => { setOpen(false); navigate("/chat"); }} className="mt-2 rounded-full bg-gp-blue text-white font-semibold">
            Start winning
          </Button>
        </div>
      )}
    </header>
  );
}
